import styled from '@emotion/styled'

import TabsBase, * as Tabs from '@segment/matcha/recipes/Tabs'
import useTabs from '@segment/matcha/recipes/Tabs/useTabs'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import ConsistentDataImage from './ConsistentDataImage'
import OptimizeSyncRunsImage from './OptimizeSyncRunsImage'
import CustomizeDataImage from './CustomizeDataImage'

const copy = {
  heading: `Clean and customizable data out-of-the-box`,
  description: `Standardize to create consistent data, customize data in each warehouse, and optimize sync runs to meet business deadlines.`,
}

const tabs = [
  {
    key: `consistent-data`,
    title: `Consistent data`,
    image: <ConsistentDataImage />,
  },
  {
    key: `customize-data`,
    title: `Customize data`,
    image: <CustomizeDataImage />,
  },
  {
    key: `optimize-data`,
    title: `Optimize sync runs`,
    image: <OptimizeSyncRunsImage />,
  },
]

export const CleanAndCustomizableData = () => {
  const { getTrackProps, getControlProps, getPanelProps, activeTabKey } = useTabs(
    0,
    tabs.length,
    `clean-and-customizable-data`
  )

  return (
    <StyledSectionBase variant="2Col" copyVariant="narrow">
      <Section.Copy alignSelf="center">
        <CopyTextBase>
          <CopyText.Heading tag="h3">{copy.heading}</CopyText.Heading>
          <CopyText.Description tag="p" variant="pLarge">
            {copy.description}
          </CopyText.Description>
        </CopyTextBase>
      </Section.Copy>
      <StyledSectionContents>
        <TabsLayout>
          <TabsBase variant="filled">
            {tabs.map((tab, index) => (
              <StyledPanel {...getPanelProps(index)} active={index === activeTabKey} key={tab.key}>
                <Slide>{tab.image}</Slide>
              </StyledPanel>
            ))}

            <StyledControls>
              <Tabs.Track {...getTrackProps()}>
                {tabs.map((tab, index) => (
                  <Tabs.Control key={tab.key} {...getControlProps(index)}>
                    {tab.title}
                  </Tabs.Control>
                ))}
              </Tabs.Track>
            </StyledControls>
          </TabsBase>
        </TabsLayout>
      </StyledSectionContents>
    </StyledSectionBase>
  )
}

const StyledSectionBase = styled(SectionBase)`
  overflow-x: visible;
  justify-content: center;

  ${Section.Wrapper} {
    row-gap: 64px;
    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      padding-bottom: 160px;
    }
    ${({ theme }) => theme.breakpoints.down(`md`)} {
      padding-bottom: 0;
    }
  }
`

const Slide = styled.div`
  display: grid;
  grid-template-rows: auto auto;
  position: relative;
  align-self: flex-start;
  align-items: center;

  ${({ theme }) => theme.breakpoints.down(`md`)} {
    max-width: 720px;
    row-gap: 48px;
  }

  ${({ theme }) => theme.breakpoints.down(`xs`)} {
    row-gap: 20px;
  }

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    grid-template-columns: auto 1fr;
    row-gap: 0;
  }
`

const TabsLayout = styled.div`
  display: grid;
  grid-template-areas:
    'tabs'
    'controls';
  gap: 50px;
  justify-items: center;

  ${({ theme }) => theme.breakpoints.down(`md`)} {
    grid-template-areas:
      'controls'
      'tabs';
  }
`

const StyledControls = styled.div`
  grid-area: controls;

  > div {
    margin: 0 auto;
  }
`

const StyledPanel = styled(Tabs.Panel)`
  grid-area: tabs;
`

const StyledSectionContents = styled(Section.Contents)`
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    display: grid;
    grid-template-columns: revert;
  }
`

export default CleanAndCustomizableData
